import React from 'react';
import { Trans } from '@lingui/react/macro';
import { Accordion, Radio, RadioGroup, SlideToggle } from '@ygtechnologies/rubus-ui';

import { DateDisplay } from '@/components/localization';
import { useOptionsScope } from '../OptionsScope';
import { ShowroomHidden } from '../../ShowroomVisibility';
import { useOptionsFilter } from '../useOptionsFilter';
import type { OptionsFilterFacets } from '../useOptionsData';
import theme from './FilterSidebar.module.scss';

interface Props {
    facets: OptionsFilterFacets;
}

export const DeadlineFilter: React.FC<Props> = ({ facets }) => {
    const { filter, setDeadline, setStatuses, setShowExpired } = useOptionsFilter();
    const { projectData } = useOptionsScope();

    const deadlines = projectData.deadlines.filter((dl) => facets.deadlineIds.includes(dl.id));
    const showSubmitted = filter.statuses.includes('Sent');

    const handleShowSubmittedChange = () => {
        if (showSubmitted) {
            setStatuses(['Default', 'Reviewed', 'Selected']);
        } else {
            setStatuses(['Default', 'Reviewed', 'Selected', 'Sent']);
        }
    };

    return (
        <Accordion
            variant="elevated"
            defaultOpen
            title={
                <span className={theme.filterTitle}>
                    <Trans>Deadline filters</Trans>
                </span>
            }>
            <div className={theme.filterBody}>
                <div className={theme.label}>
                    <Trans>Filter by deadline</Trans>
                </div>
                <RadioGroup>
                    <Radio
                        inputSize="md"
                        name="deadline-filter"
                        checked={filter.deadline === null}
                        onChange={() => setDeadline(null)}>
                        <Trans>Show all</Trans>
                    </Radio>
                    {deadlines.map((dl) => (
                        <Radio
                            inputSize="md"
                            key={dl.id}
                            name="deadline-filter"
                            checked={filter.deadline === dl.id}
                            onChange={() => setDeadline(dl.id)}>
                            {dl.name} - <DateDisplay value={dl.deadline} />
                        </Radio>
                    ))}
                </RadioGroup>

                {/* In the showroom signatures and deadlines are irrelevant: keep the defaults (don't
                    show submitted items, always show expired ones) and hide the toggles entirely. */}
                <ShowroomHidden>
                    <div className={theme.toggleRow}>
                        <span>
                            <Trans>Show submitted items</Trans>
                        </span>
                        <SlideToggle checked={showSubmitted} onChange={handleShowSubmittedChange} />
                    </div>

                    <div className={theme.toggleRow}>
                        <span>
                            <Trans>Show expired items</Trans>
                        </span>
                        <SlideToggle
                            checked={filter.showExpired}
                            onChange={(e) => setShowExpired(e.target.checked)}
                        />
                    </div>
                </ShowroomHidden>
            </div>
        </Accordion>
    );
};
